import { ACCESS } from '@/features/access/access';
import { useRoleAccess } from '@/features/access/use-role-access';
import { useNavigate } from '@tanstack/react-router';
import { AlertBanner, AlertBannerAction } from '@/components/molecules/AlertBanner';
import { splitLeadSentence } from '@/components/molecules/alert-banner-helpers';
import { Gauge } from '@/icons/Icon';
import { cn } from '@/lib/utils';
import { useOrgRole } from '@/features/settings/queries';
import { useBillingSummary } from './queries';
import {
  USAGE_BANNER_VARIANT,
  USAGE_UPGRADE_LABEL,
  USAGE_UPGRADE_ROUTE,
  usageBannerMessage,
  usageNudgeCtaVisible,
} from './usage-nudge';
import { trackTrialBannerInteracted } from './trial-banner-analytics';

/**
 * The app-wide USAGE banner (TRU-540). Mounted in `AppShell` directly BELOW
 * `BillingAlertBanner`, and deliberately not merged into it.
 *
 * The two are INDEPENDENT surfaces answering different questions — "is your
 * subscription healthy?" vs "are you running out of room?" — and a single org
 * can be in trouble on both at once. Folding this into `deriveBanner`'s
 * precedence chain would mean a PAST_DUE org silently loses its "you're at 95%
 * of your mailboxes" warning behind the payment banner, which is exactly the
 * case where hitting a hard limit is most surprising. So they stack: the backend
 * emits `banner` and `usageBanner` as separate fields, and each component reads
 * only its own.
 *
 * Like its sibling, a loading or failed summary renders nothing — a nudge must
 * never block, shift, or error the app it sits on top of (the sidebar `PlanCard`
 * owns the visible failure state for this shared query key). `APPROACHING` never
 * reaches here: the backend emits no `usageBanner` at 70%, which is what makes
 * that tier "subtle indicator only".
 */
export function UsageAlertBanner({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { data } = useBillingSummary();
  const { isOrgOwner } = useOrgRole(); // copy only
  const { allowed: canManageBilling } = useRoleAccess(ACCESS.billingManage);

  const banner = data?.usageBanner ?? null;
  if (!banner) return null;

  const showCta = usageNudgeCtaVisible(canManageBilling);

  function goToPlans() {
    // Same PostHog event the sidebar `PlanCard` and the billing banner fire (the
    // name is fixed and shared); `kind` distinguishes which surface it came from.
    trackTrialBannerInteracted(data, 'upgrade_clicked', banner?.kind);
    void navigate({ to: USAGE_UPGRADE_ROUTE, search: { tab: 'plans' } });
  }

  // The first sentence ("You've used 90% of …") leads in bold; copy unchanged.
  const { lead, rest } = splitLeadSentence(usageBannerMessage(banner, isOrgOwner));

  return (
    <AlertBanner
      variant={USAGE_BANNER_VARIANT[banner.kind]}
      icon={Gauge}
      title={lead}
      appearance="solid"
      data-usage-banner-kind={banner.kind}
      className={cn('shrink-0', className)}
      action={
        showCta ? (
          <AlertBannerAction onClick={goToPlans}>{USAGE_UPGRADE_LABEL}</AlertBannerAction>
        ) : undefined
      }
    >
      {rest}
    </AlertBanner>
  );
}
